import { ActionMenu, DotMenuHorizontalIcon, theme, DeprecatedChevronDownIcon } from '@arnold/common';
import styled from '@emotion/styled';
import React, { ForwardedRef, forwardRef } from 'react';

interface Props {
  expanded?: boolean;
  title: string;
  headerItems?: {
    title: string;
    dataCy?: string;
  }[];
  contextItems?: {
    label: string;
    dataCy?: string;
    action: () => void;
  }[];
  children: React.ReactNode;
  onExpandClicked?: () => void;
  onCollapseClicked?: () => void;
  highlightTitle?: boolean;
  dataCy?: string;
  dataIcom?: string;
  onRowClick?: () => void;
}

interface HeaderProps {
  onClick?: () => void;
}

export const CollapsibleContainer = forwardRef(
  (
    {
      expanded,
      title,
      headerItems,
      contextItems,
      children,
      onExpandClicked,
      onCollapseClicked,
      highlightTitle,
      dataCy,
      dataIcom,
      onRowClick,
    }: Props,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const handleExpandCollapse = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      if (expanded) {
        onCollapseClicked && onCollapseClicked();
      } else {
        onExpandClicked && onExpandClicked();
      }
    };

    return (
      <Container data-cy={dataCy} data-icom={dataIcom} ref={ref}>
        <Header data-cy="collapsible-container-header" onClick={onRowClick}>
          <IconAndTitleWrapper onClick={handleExpandCollapse}>
            {(onExpandClicked || onCollapseClicked) && <StyledDownChevron expanded={expanded} />}
            <Title hasIcon={!!(onExpandClicked || onCollapseClicked)} highlighted={highlightTitle}>
              {title}
            </Title>
          </IconAndTitleWrapper>
          <HeaderItems>
            {headerItems &&
              headerItems.map((item, index) => (
                <HeaderItem key={index} data-cy={item.dataCy}>
                  {item.title}
                </HeaderItem>
              ))}
          </HeaderItems>
          {contextItems && contextItems.length > 0 && (
            <ActionMenuWrapper onClick={(event) => event.stopPropagation()}>
              <ActionMenu
                items={contextItems}
                toggleIcon={<DotMenuHorizontalIcon />}
                align="right"
                noMargin
                noBackground
                menuShiftX={2}
                menuShiftY={46}
              />
            </ActionMenuWrapper>
          )}
        </Header>
        {expanded && <ChildrenWrapper>{children}</ChildrenWrapper>}
      </Container>
    );
  },
);

const Container = styled.div`
  margin: 8px 0;
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.s};
  & & {
    margin: 8px 16px;
  }
`;

const Header = styled.div<HeaderProps>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  background-color: ${theme.colors.backgroundBasic.default};
  color: ${theme.colors.text.primary};
  position: relative;
  border-radius: ${theme.borderRadius.s};
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
`;

const IconAndTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  padding-right: 0;
  cursor: pointer;
`;

const StyledDownChevron = styled(DeprecatedChevronDownIcon)<{ expanded?: boolean }>`
  cursor: pointer;
  margin-left: 8px;
  transform: ${({ expanded }) => (expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Title = styled.div<{ hasIcon?: boolean; highlighted?: boolean }>`
  color: ${theme.colors.brand.primary};
  font-weight: bold;
  cursor: pointer;
  margin-left: ${({ hasIcon }) => (hasIcon ? '0' : '12px')};

  ${({ highlighted }) =>
    highlighted &&
    `
      background-color: ${theme.colors.emotionWarning.background};
    border-radius:  ${theme.borderRadius.s};
    outline: 1px solid ${theme.colors.emotionWarning.default};
    padding: 0 ${theme.spacing.d};
    line-height: 25px;
      margin-left: -${theme.spacing.c};
      margin-right: -${theme.spacing.c};
    `}
`;

const HeaderItems = styled.div`
  display: flex;
  margin-left: 8px;
  gap: 20px;
  font-size: ${theme.typography.body.small.regular.fontSize};
`;

const HeaderItem = styled.div``;

const ActionMenuWrapper = styled.span`
  margin-left: auto;
`;

const ChildrenWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;
